/**
 * FieldSpec (сгенерированный из node definition) -> контрол UI kit.
 *
 * Подписи, описания и человекочитаемые названия значений enum берутся из словарей
 * `src/content/model-docs/*` по `id_global`. В node definition их нет: там только
 * name / dataType / value / options / optional.
 */
import { useRef, useState } from "react";
import { api } from "../../api/client";
import { useI18n } from "../../i18n/I18nProvider";
import { Button, FieldShell, Input, Select, Switch, Textarea, UploadIcon } from "../../ui-kit";
import { resolveField } from "./fieldText";
import type { FieldSpec } from "../../api/types";

export type FieldValue = unknown;

/**
 * Кнопка загрузки файла рядом с полем-файлом.
 *
 * Файловый вход принимает и публичную ссылку, и id уже загруженного объекта, а
 * своего хостинга у пользователя может не быть — поэтому кнопка кладёт файл в его
 * хранилище через `/api/uploads` и подставляет в поле `storage_object_id`.
 */
function UploadButton({
  accept,
  onUploaded,
  onError,
}: {
  accept: string | undefined;
  onUploaded: (fileId: number) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={async (event) => {
          const file = event.target.files?.[0];
          // Сбрасываем сразу: иначе повторный выбор того же файла не даст change.
          event.target.value = "";
          if (!file) return;
          setBusy(true);
          onError(null);
          try {
            onUploaded((await api.upload(file)).storage_object_id);
          } catch (cause) {
            onError(cause instanceof Error ? cause.message : String(cause));
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button
        variant="tertiary"
        loading={busy}
        startIcon={<UploadIcon />}
        onClick={() => inputRef.current?.click()}
      >
        {t("model.upload")}
      </Button>
    </>
  );
}

/** Поле и кнопка загрузки в одну строку — пока она помещается. */
function withUpload(control: React.ReactNode, button: React.ReactNode) {
  return (
    // На узком экране кнопка уезжает под поле: рядом с ней полю оставалось бы
    // меньше половины ширины, и вставленная ссылка переставала читаться.
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-2)", alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 200px", minWidth: 0 }}>{control}</div>
      {button}
    </div>
  );
}

interface FieldControlProps {
  idGlobal: string;
  field: FieldSpec;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  error?: string;
}

/** Список ссылок хранится в форме как текст (по одной в строке) — так его удобно вставлять. */
const linesToArray = (text: string): string[] =>
  text.split("\n").map((line) => line.trim()).filter(Boolean);

const arrayToLines = (value: FieldValue): string =>
  Array.isArray(value) ? value.map(String).join("\n") : typeof value === "string" ? value : "";

export function FieldControl({ idGlobal, field, value, onChange, error }: FieldControlProps) {
  const { t, fieldDoc } = useI18n();
  const resolved = resolveField(field, fieldDoc(idGlobal, field.kind, field.name));
  const [uploadError, setUploadError] = useState<string | null>(null);
  const accept = field.accept?.length ? field.accept.join(",") : undefined;

  const { label, description } = resolved;
  const inputId = `${field.kind}-${field.name}`;

  const hint = [
    description,
    field.control === "asset" ? t("model.assetHint") : undefined,
    field.control === "asset_list" || field.control === "list" ? t("model.arrayHint") : undefined,
    field.max_items ? `max ${field.max_items}` : undefined,
    field.range?.min != null || field.range?.max != null
      ? `${field.range?.min ?? "−∞"} … ${field.range?.max ?? "∞"}`
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const shell = (children: React.ReactNode) => (
    <FieldShell
      label={label}
      hint={hint || undefined}
      error={error || uploadError || undefined}
      required={field.required}
      htmlFor={inputId}
    >
      {children}
    </FieldShell>
  );

  switch (field.control) {
    case "switch":
      return shell(
        <Switch id={inputId} checked={Boolean(value)} onChange={onChange} />,
      );

    case "number":
      return shell(
        <Input
          id={inputId}
          type="number"
          invalid={Boolean(error)}
          value={value === null || value === undefined ? "" : String(value)}
          min={field.range?.min ?? undefined}
          max={field.range?.max ?? undefined}
          step={field.range?.step ?? undefined}
          onChange={(event) =>
            onChange(event.target.value === "" ? null : Number(event.target.value))
          }
        />,
      );

    case "select":
      // список уже сужен до значений, разрешённых витриной (allowed_values)
      return shell(
        <Select
          id={inputId}
          invalid={Boolean(error)}
          value={value === null || value === undefined ? null : String(value)}
          options={resolved.options}
          onChange={onChange}
        />,
      );

    case "textarea":
      return shell(
        <Textarea
          id={inputId}
          invalid={Boolean(error)}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "asset_list":
      return shell(
        withUpload(
          <Textarea
            id={inputId}
            invalid={Boolean(error)}
            value={arrayToLines(value)}
            onChange={(event) => onChange(linesToArray(event.target.value))}
          />,
          <UploadButton
            accept={accept}
            onError={setUploadError}
            onUploaded={(fileId) =>
              onChange([...(Array.isArray(value) ? value.map(String) : []), String(fileId)])
            }
          />,
        ),
      );

    case "list":
      return shell(
        <Textarea
          id={inputId}
          invalid={Boolean(error)}
          value={arrayToLines(value)}
          onChange={(event) => onChange(linesToArray(event.target.value))}
        />,
      );

    case "json":
      return shell(
        <Textarea
          id={inputId}
          invalid={Boolean(error)}
          value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)}
          onChange={(event) => onChange(event.target.value)}
        />,
      );

    case "asset":
      return shell(
        withUpload(
          <Input
            id={inputId}
            invalid={Boolean(error)}
            value={typeof value === "string" ? value : value == null ? "" : String(value)}
            placeholder="https://…"
            onChange={(event) => onChange(event.target.value)}
          />,
          <UploadButton
            accept={accept}
            onError={setUploadError}
            onUploaded={(fileId) => onChange(String(fileId))}
          />,
        ),
      );

    case "text":
    default:
      return shell(
        <Input
          id={inputId}
          invalid={Boolean(error)}
          value={typeof value === "string" ? value : value == null ? "" : String(value)}
          onChange={(event) => onChange(event.target.value)}
        />,
      );
  }
}
